import { useConfig } from 'config';
import { ShopId } from 'constants/shop';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { usePetProfilesStore } from 'stores/pet-profiles';
import { useUserSegmentationStore } from 'stores/user-segmentation';
import { gtm } from 'tracking/gtm';
import convertProfileToArray from 'utils/pet-profile/convert-profile-to-array';
import { usePetContext } from './pet';
import { useSegmentationContext } from './segmentation';

type GaSegmentation = {
  /** Last pushed segmentation  */
  lastSegment?: string;
  /** petProfile, joined string of segmentation   */
  petProfile: string;
  /** allSegments, joined string of segmentation   */
  allSegments: string;
};

interface TrackingContextType {
  trackHotjarEvent: (
    eventName?: string,
    eventProperties?: Record<string, unknown>
  ) => void;
}

const TrackingContext = createContext<TrackingContextType>({} as any);

/**
 * A context used to track list names for performance analysis purposes
 */
export const TrackingContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { shopId } = useConfig();

  const { dogProfile, catProfile, petProfile } = usePetProfilesStore(
    (state) => ({
      dogProfile: state.dogProfile,
      catProfile: state.catProfile,
      petProfile: state.petProfile,
    })
  );

  /**
   * Push segmentation to gtm
   */
  const { multipleSegmentation, lastSegmentation, allPetSegments } =
    useSegmentationContext();
  const { segments: userSegments } = useUserSegmentationStore();

  const { current: cookiePetValue } = usePetContext();

  useEffect(() => {
    const _petType = cookiePetValue;

    const dogProfileArray = dogProfile
      ? convertProfileToArray({ petProfile: dogProfile })
      : [];
    const catProfileArray = catProfile
      ? convertProfileToArray({ petProfile: catProfile })
      : [];

    const newVal: GaSegmentation = {
      allSegments: [
        ...new Set([
          ...dogProfileArray,
          ...catProfileArray,
          ...userSegments,
          ...allPetSegments,
        ]),
      ]
        .sort()
        .join(','),
      petProfile: [
        ...new Set([...dogProfileArray, ...catProfileArray, ...allPetSegments]),
      ]
        .sort()
        .join(','),
      lastSegment: lastSegmentation,
    };

    gtm({
      session: {
        ...newVal,
        ...(typeof _petType === 'boolean' ? {} : { petType: _petType }),
      },
    });
  }, [
    catProfile,
    cookiePetValue,
    dogProfile,
    lastSegmentation,
    multipleSegmentation,
    petProfile,
    userSegments,
    allPetSegments,
  ]);

  /**
   * Utility function to trigger Hotjar events
   * It will only trigger the event if the cookie value is 'dogs'
   * Mainly used to track user behavior with the Dynamic UX
   */
  const trackHotjarEvent = useCallback(
    (
      eventName: string = 'start_hotjar',
      eventProperties?: Record<string, unknown>
    ): void => {
      try {
        // Check if Hotjar is available
        if (
          typeof window !== 'undefined' &&
          window.hj &&
          cookiePetValue === 'dogs'
        ) {
          if (eventProperties) {
            window.hj('event', eventName, eventProperties);
          } else {
            console.log('Tracking Hotjar event:', eventName);
            window.hj('event', eventName);
          }
        }
      } catch (error) {
        console.error('Error tracking Hotjar event:', error);
      }
    },
    [cookiePetValue]
  );

  /**
   * Track Hotjar event for CH
   */
  useEffect(() => {
    if (shopId === ShopId.CH) {
      trackHotjarEvent('pdex_ch');
    }
  }, [shopId, trackHotjarEvent]);

  return (
    <TrackingContext.Provider
      value={{
        trackHotjarEvent,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export const useTrackingContext = (): TrackingContextType =>
  useContext(TrackingContext);
